import { Text, Button, Badge, Tag } from '@fluentui/react-components';
import { useIntl } from 'react-intl';
import { ArrowLeftRegular, FolderRegular, DocumentRegular, ChatRegular, ClipboardRegular, MoneyRegular, ClipboardCheckmarkRegular, BuildingRegular, SlideTextRegular } from '@fluentui/react-icons';
import { CafPhaseTracker } from '../components/CafPhaseTracker';
import type { Project, EngagementTypeOption } from '../services/project-service';
import type { MethodologyDocument } from '../services/document-service';
import type { PresentationDocument } from '../services/presentation-service';
import { auditControlCount } from './AuditNavigatorPage';
import { getBadgeProps, DOC_STATUS_BADGE, PROJECT_STATUS_BADGE } from '../utils/badge-config';

const PHASE_ORDER = ['governance', 'strategy', 'plan', 'ready', 'adopt', 'review', 'misc'];

interface ProjectDetailPageProps {
  project: Project;
  documents: MethodologyDocument[];
  presentations?: PresentationDocument[];
  engagementTypes?: EngagementTypeOption[];
  onBack: () => void;
  onSelectDocument: (doc: MethodologyDocument) => void;
  onSelectPresentation?: (deck: PresentationDocument) => void;
  onStartAssessment?: () => void;
  onOpenRegisters?: () => void;
  onOpenCostEstimate?: () => void;
  onOpenAuditNavigator?: () => void;
  onOpenCustomerSettings?: () => void;
}

export function ProjectDetailPage({ project, documents, presentations = [], engagementTypes = [], onBack, onSelectDocument, onSelectPresentation, onStartAssessment, onOpenRegisters, onOpenCostEstimate, onOpenAuditNavigator, onOpenCustomerSettings }: ProjectDetailPageProps) {
  const intl = useIntl();
  const engagementTypeLabel = (id: string) =>
    engagementTypes.find(t => t.id === id)?.displayName ?? id;
  // Group documents by CAF phase
  const docsByPhase = PHASE_ORDER
    .map(phase => ({
      phase,
      docs: documents.filter(d => d.cafPhase === phase),
    }))
    .filter(g => g.docs.length > 0);

  return (
    <div className="page-root page-root--scrollable project-detail-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'projectDetail.btn.back.title' })} />
        <FolderRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{project.name}</Text>
        <div className="title-bar-actions">
          {onOpenRegisters && (
            <Button appearance="subtle" icon={<ClipboardRegular />} onClick={onOpenRegisters} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.registers' })}
            </Button>
          )}
          {onOpenCostEstimate && (
            <Button appearance="subtle" icon={<MoneyRegular />} onClick={onOpenCostEstimate} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.costEstimate' })}
            </Button>
          )}
          {onOpenAuditNavigator && (
            <Button appearance="subtle" icon={<ClipboardCheckmarkRegular />} onClick={onOpenAuditNavigator} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.auditNavigator' })}
            </Button>
          )}
          {onOpenCustomerSettings && (
            <Button appearance="subtle" icon={<BuildingRegular />} onClick={onOpenCustomerSettings} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.customerDetails' })}
            </Button>
          )}
          {onStartAssessment && (
            <Button appearance="primary" icon={<ChatRegular />} onClick={onStartAssessment} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.caseAssessment' })}
            </Button>
          )}
          <Badge size="small" {...getBadgeProps(PROJECT_STATUS_BADGE, project.status)}>{project.status}</Badge>
        </div>
      </div>

      <CafPhaseTracker currentPhase={project.cafPhase} documents={documents} />

      <div className="project-meta">
        <div className="meta-grid">
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.customer' })}</span><span>{project.customerId}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.scope' })}</span><span>{project.scope}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.region' })}</span><span>{project.azureRegion}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.hypervisor' })}</span><span>{project.hypervisor}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.documents' })}</span><span>{documents.length}</span></div>
          <div className="meta-item">
            <span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.engagementType' })}</span>
            <span>
              {(project.engagementTypes ?? []).length === 0
                ? <span className="text-muted">—</span>
                : (
                  <span className="tag-chip-row">
                    {(project.engagementTypes ?? []).map(id => (
                      <Tag key={id} size="extra-small" appearance="brand">{engagementTypeLabel(id)}</Tag>
                    ))}
                  </span>
                )}
            </span>
          </div>
        </div>
      </div>

      <div className="table-scroll-container">
        {docsByPhase.map(({ phase, docs }) => (
          <div key={phase} className="phase-group">
            <div className="section-header">
              <Text size={400} weight="semibold">{phase.charAt(0).toUpperCase() + phase.slice(1)}</Text>
              <Badge size="small" appearance="outline">{docs.length}</Badge>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.document' })}</th>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.template' })}</th>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.status' })}</th>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.version' })}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  const ctrlCount = auditControlCount(doc);
                  return (
                    <tr key={doc.id} className="clickable" onClick={() => onSelectDocument(doc)}>
                      <td>
                        <div className="flex-row">
                          <DocumentRegular />
                          <Text weight="semibold">{doc.title}</Text>
                          <Badge
                            size="extra-small"
                            appearance="outline"
                            color={ctrlCount > 0 ? 'informative' : undefined}
                            title={ctrlCount === 0
                            ? intl.formatMessage({ id: 'projectDetail.auditBadge.titleNone' })
                            : intl.formatMessage({ id: 'projectDetail.auditBadge.titleCount' }, { count: ctrlCount })}
                          >
                            {intl.formatMessage({ id: 'projectDetail.auditBadge.label' }, { count: ctrlCount })}
                          </Badge>
                        </div>
                      </td>
                      <td className="text-muted control-ref">{doc.templateId}</td>
                      <td><Badge size="small" {...getBadgeProps(DOC_STATUS_BADGE, doc.status)}>{doc.status}</Badge></td>
                      <td className="text-muted">v{doc.version}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        {documents.length === 0 && (
          <div className="page-empty">
            <Text className="text-muted">{intl.formatMessage({ id: 'projectDetail.empty.documents' })}</Text>
          </div>
        )}
      </div>

      {/* Presentations */}
      {presentations.length > 0 && (
        <div className="presentations-section">
          <div className="section-header">
            <Text size={400} weight="semibold">{intl.formatMessage({ id: 'projectDetail.presentations.heading' })}</Text>
            <Badge size="small" appearance="outline">{presentations.length}</Badge>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.deck' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.session' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.template' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.slides' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.status' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.lastRendered' })}</th>
              </tr>
            </thead>
            <tbody>
              {[...presentations]
                .sort((a, b) => (a.sessionNumber ?? 999) - (b.sessionNumber ?? 999))
                .map(deck => (
                  <tr
                    key={deck.id}
                    className={onSelectPresentation ? 'clickable' : undefined}
                    onClick={() => onSelectPresentation?.(deck)}
                  >
                    <td>
                      <div className="flex-row">
                        <SlideTextRegular />
                        <Text weight="semibold">{deck.title}</Text>
                      </div>
                    </td>
                    <td className="text-muted">{deck.sessionNumber ? intl.formatMessage({ id: 'projectDetail.presentations.session' }, { number: deck.sessionNumber }) : '—'}</td>
                    <td className="text-muted control-ref">{deck.templateId}</td>
                    <td className="text-muted">{deck.slideCount ?? '—'}</td>
                    <td><Badge size="small" {...getBadgeProps(DOC_STATUS_BADGE, deck.status)}>{deck.status}</Badge></td>
                    <td className="text-muted">{deck.lastRenderedAt ? new Date(deck.lastRenderedAt).toLocaleString() : intl.formatMessage({ id: 'projectDetail.presentations.neverRendered' })}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Snapshots Timeline */}
      {project.snapshots && project.snapshots.length > 0 && (
        <div className="snapshots-section">
          <div className="section-header">
            <Text size={400} weight="semibold">{intl.formatMessage({ id: 'projectDetail.snapshots.heading' })}</Text>
            <Badge size="small" appearance="outline">{project.snapshots.length}</Badge>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.version' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.label' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.date' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.workloads' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.facts' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.risks' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.estMonthly' })}</th>
              </tr>
            </thead>
            <tbody>
              {[...project.snapshots].reverse().map(s => (
                <tr key={s.version}>
                  <td><Badge size="small" appearance="outline">v{s.version}</Badge></td>
                  <td>{s.label}</td>
                  <td className="text-muted">{new Date(s.timestamp).toLocaleDateString()}</td>
                  <td>{s.workloadCount}</td>
                  <td>{s.factCount}</td>
                  <td>{s.riskCount}</td>
                  <td>{s.grandTotalMonthly ? `$${s.grandTotalMonthly.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
